import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/* The brand reveal: one gesture, a rise plus a fade.
   travel 2.25rem · transform 1000ms · opacity 620ms · cubic-bezier(.33,1,.68,1)
   Opacity finishes before the movement, so the element is readable while it settles.

   Five rules from the standards, each enforced below:

   1. The hidden state is applied by JS, never by base CSS. If this module fails
      to run, the page is still fully readable. Nothing here authors opacity:0
      into a stylesheet.
   2. Never transform a sticky element — `transform` makes it a containing block
      and silently kills `position: sticky`. Such elements fade only. The same
      trap applies to any ancestor of a `position: fixed` child, so those are
      skipped too.
   3. Never reveal anything that starts hidden. The observer never fires for a
      display:none element, so it would stay invisible when opened.
   4. Fire slightly early — threshold 0 and a -4% bottom margin — so a long
      travel has room to play.
   5. Honour prefers-reduced-motion: everything resolves to its settled state.

   Screens opt in with `data-reveal` (one element) or `data-reveal-group`
   (stagger the children) rather than by being wrapped in a component. Adding an
   attribute cannot unbalance JSX; swapping tags in a 1,000-line file can. */

const MAX_STAGGER = 700;

const REDUCED = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isHidden = (el) => {
  let cur = el;
  while (cur && cur !== document.documentElement) {
    const cs = getComputedStyle(cur);
    if (cs.display === "none" || cs.visibility === "hidden") return true;
    cur = cur.parentElement;
  }
  return false;
};

/* A transform on an ancestor re-parents position:fixed children to it. Fading
   such an element is safe; moving it would drag modals and floating buttons. */
const holdsFixedChild = (el) =>
  [...el.querySelectorAll("*")].some((c) => getComputedStyle(c).position === "fixed");

const hide = (el) => {
  const cs = getComputedStyle(el);
  const fadeOnly = cs.position === "sticky" || cs.position === "fixed" || holdsFixedChild(el);
  el.style.opacity = "0";
  if (!fadeOnly) el.style.transform = "translateY(var(--ns-reveal-travel))";
  el.style.willChange = fadeOnly ? "opacity" : "opacity, transform";
  el.dataset.revealState = "hidden";
};

const show = (el, delay) => {
  el.style.transition =
    `opacity var(--ns-reveal-opacity-duration) var(--ns-ease-reveal) ${delay}ms, ` +
    `transform var(--ns-reveal-transform-duration) var(--ns-ease-reveal) ${delay}ms`;
  el.style.opacity = "1";
  el.style.transform = "translateY(0)";
  el.dataset.revealState = "shown";
  const clear = () => {
    el.style.willChange = "";
    el.style.transition = "";
    el.style.transform = "";
    el.removeEventListener("transitionend", clear);
  };
  el.addEventListener("transitionend", clear);
};

const makeObserver = () =>
  new IntersectionObserver(
    (entries, io) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        show(entry.target, Number(entry.target.dataset.revealDelay || 0));
        io.unobserve(entry.target);
      });
    },
    { threshold: 0, rootMargin: "0px 0px -4% 0px" }
  );

const collect = (root) => {
  const targets = [];
  root.querySelectorAll("[data-reveal]").forEach((el) => {
    if (!el.dataset.revealState && !isHidden(el)) {
      el.dataset.revealDelay = el.dataset.reveal || "0";
      targets.push(el);
    }
  });
  root.querySelectorAll("[data-reveal-group]").forEach((group) => {
    const kids = [...group.children].filter((el) => !el.dataset.revealState && !isHidden(el));
    if (!kids.length) return;
    // Dense groups move quickly, sparse ones linger — 90ms to 190ms.
    const step = Number(group.dataset.revealGroup) || (kids.length > 5 ? 90 : 190);
    kids.forEach((el, i) => {
      // The per-sibling step is what the eye reads, but a long list would leave
      // the tail waiting seconds (17 cards x 90ms = 1.5s). Clamp the delay so
      // late items arrive together rather than trickling.
      el.dataset.revealDelay = String(Math.min(i * step, MAX_STAGGER));
      targets.push(el);
    });
  });
  targets.forEach(hide);
  return targets;
};

/** Mounted once. Watches for opted-in elements and reveals them on scroll. */
export const RevealScanner = () => {
  const location = useLocation();
  const ioRef = useRef(null);

  useEffect(() => {
    if (REDUCED()) return undefined;

    const io = makeObserver();
    ioRef.current = io;

    const scan = () => collect(document).forEach((el) => io.observe(el));
    scan();

    // Content arrives after fetches and route changes, so keep watching.
    const mo = new MutationObserver(() => scan());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, [location.pathname]);

  return null;
};

export default RevealScanner;
